import type { EventData } from "@/lib/events/types"

import { eventLocationCoverCityRegionLine } from "@/lib/location/display"

const MAX_COVER_LOC_LINE = 48

type CoverLocationFields = Pick<
  EventData,
  "location" | "locationName" | "address"
>

function trim(s: string | undefined | null): string {
  return s?.trim() ?? ""
}

/** Same policy as `display.ts` for trailing country tokens, plus a few common ISO names for cover line2. */
function isLikelyCountrySuffix(part: string): boolean {
  const p = part.toLowerCase()
  return (
    p === "united states" ||
    p === "usa" ||
    p === "united states of america" ||
    p === "uk" ||
    p === "u.k." ||
    p === "united kingdom" ||
    p === "canada" ||
    p === "australia" ||
    p === "japan" ||
    p === "france" ||
    p === "germany" ||
    p === "mexico" ||
    p === "brazil" ||
    p === "italy" ||
    p === "spain" ||
    p === "india" ||
    p === "china"
  )
}

function stripTrailingCountrySegments(line: string): string {
  const parts = line.split(",").map((s) => s.trim()).filter(Boolean)
  const out = [...parts]
  while (out.length && isLikelyCountrySuffix(out[out.length - 1]!)) {
    out.pop()
  }
  return out.join(", ").trim()
}

function clipLine(s: string): string {
  const t = s.trim()
  if (t.length <= MAX_COVER_LOC_LINE) return t
  return `${t.slice(0, MAX_COVER_LOC_LINE - 1).trimEnd()}…`
}

function firstCommaSegment(formattedAddress: string): string {
  const f = formattedAddress.trim()
  const i = f.indexOf(",")
  return (i === -1 ? f : f.slice(0, i)).trim()
}

/** Strip a leading US-style house number (and simple hyphen ranges) from the first address line. */
function stripLeadingHouseNumberFromStreetLine(line: string): string {
  const t = line.trim()
  const stripped = t
    .replace(/^(?:\d+[A-Za-z]?(?:\s*-\s*\d+)?\s+)+/, "")
    .trim()
  return stripped.length > 0 ? stripped : t
}

function locationNameIsVenueVersusStreetHead(
  locationName: string,
  addressHead: string,
): boolean {
  const name = locationName.trim().toLowerCase()
  const head = addressHead.trim().toLowerCase()
  if (!name) return false
  if (!head) return true
  const streetOnly = stripLeadingHouseNumberFromStreetLine(addressHead)
    .trim()
    .toLowerCase()
  if (name === head || name === streetOnly) return false
  return true
}

function coverVenueIsPrimary(name: string, addr: string, loc: string): boolean {
  if (!name) return false
  if (addr) {
    return locationNameIsVenueVersusStreetHead(name, firstCommaSegment(addr))
  }
  if (loc && name.toLowerCase() === loc.toLowerCase()) return false
  return true
}

/**
 * Raw two-line cover location (no per-line character cap). Used to build a single-line
 * hero string; CSS `text-overflow: ellipsis` handles overflow in the UI.
 */
function buildCoverLocationLinesUnclipped(
  e: CoverLocationFields,
): { line1: string; line2: string } {
  const name = trim(e.locationName)
  const addr = trim(e.address)
  const loc = trim(e.location)
  const cityRegion = stripTrailingCountrySegments(
    eventLocationCoverCityRegionLine(e).trim(),
  )

  const venueIsPrimary = coverVenueIsPrimary(name, addr, loc)

  if (venueIsPrimary) {
    const line2 =
      cityRegion && cityRegion.toLowerCase() !== name.toLowerCase()
        ? cityRegion
        : ""
    return { line1: name, line2: line2 }
  }

  return { line1: cityRegion || "", line2: "" }
}

/**
 * One-line cover location: joins coarse lines with ", " (venue + city/region when applicable).
 * Same selection rules as {@link formatCoverLocationLines}; no street-level segments.
 */
export function formatCoverLocationHeroSingleLine(
  e: CoverLocationFields,
): string {
  const { line1, line2 } = buildCoverLocationLinesUnclipped(e)
  const a = line1.trim()
  const b = line2.trim()
  if (a && b) return `${a}, ${b}`
  return a || b
}

/**
 * Short, human-readable location for the public event cover only (never street, full postal, ZIP, or country).
 * Prefer venue/place name with city + region beneath; otherwise city + region (or city alone) from address/location.
 */
export function formatCoverLocationLines(
  e: CoverLocationFields,
): { line1: string; line2: string } {
  const raw = buildCoverLocationLinesUnclipped(e)
  return {
    line1: clipLine(raw.line1),
    line2: clipLine(raw.line2),
  }
}
